type Party = { label: string; logoUrl: string };

interface MemberCardProps {
  initials: string;
  name: string;
  role: string;
  variant: 'featured' | 'officer' | 'member';
  mandate?: string; // e.g. "2021 – 2025", shown only on featured card
  photo?: string; // URL to member photo; falls back to a monogram if absent
  party?: Party; // Political affiliation — logo + label chip (Assembly cards).
}

// Logo + label chip identifying a member's political party.
function PartyBadge({ party }: { party: Party }) {
  return (
    <span className="border-line bg-background text-on-surface-light inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium">
      <img src={party.logoUrl} alt="" className="h-4 w-4 rounded" />
      {party.label}
    </span>
  );
}

// Neutral Fraunces monogram (flat brand tint — no gradient) shown when there's
// no photo. Kept consistent across variants; only size/radius differ.
const mono =
  'bg-primary/8 text-primary border-primary/15 font-headline flex items-center justify-center border font-semibold';

const LONG_NAME_THRESHOLD = 30;
const NAME_PARTICLES = new Set(['da', 'das', 'de', 'do', 'dos', 'e']);

function displayName(name: string): string {
  if (name.length <= LONG_NAME_THRESHOLD) return name;

  const parts = name.trim().split(/\s+/);
  if (parts.length < 3) return name;

  const middleName = parts.slice(1, -1).find((part) => !NAME_PARTICLES.has(part.toLowerCase()));
  return [parts[0], middleName, parts[parts.length - 1]].filter(Boolean).join(' ');
}

// ── Featured variant (Presidente) ─────────────────────────────────────────────
function FeaturedCard({ initials, name, role, mandate, photo, party }: MemberCardProps) {
  return (
    <div className="bg-surface border-line relative w-full max-w-72 overflow-hidden rounded-2xl border p-8 text-center shadow-sm">
      <div className="bg-gold-leaf absolute top-0 right-0 left-0 h-[3px]" />

      {photo ? (
        <img
          src={photo}
          alt={name}
          loading="lazy"
          decoding="async"
          width="96"
          height="96"
          className="mx-auto mb-5 h-24 w-24 rounded-full object-cover object-top"
        />
      ) : (
        <div className={`${mono} mx-auto mb-5 h-24 w-24 rounded-full text-3xl`}>{initials}</div>
      )}

      <span className="border-primary/20 bg-primary/10 text-primary mb-3 inline-block rounded-full border px-3.5 py-1 text-[10px] font-bold tracking-widest uppercase">
        {role}
      </span>

      <p className="font-headline text-on-surface mb-1 text-xl font-bold" title={name}>
        {displayName(name)}
      </p>

      {party && (
        <div className="mt-3 flex justify-center">
          <PartyBadge party={party} />
        </div>
      )}

      {mandate && (
        <div className="border-line bg-background mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1">
          <span className="bg-tertiary h-1.5 w-1.5 rounded-full" />
          <span className="text-on-surface-light text-[11px]">Mandato {mandate}</span>
        </div>
      )}
    </div>
  );
}

// ── Officer variant (Vice-Presidente, Tesoureiro, Secretário) ─────────────────
function OfficerCard({ initials, name, role, photo, party }: MemberCardProps) {
  return (
    <div className="bg-surface border-line relative flex items-center gap-5 overflow-hidden rounded-xl border p-6 shadow-sm">
      <div className="bg-gold-leaf absolute top-0 bottom-0 left-0 w-[3px]" />

      {photo ? (
        <img
          src={photo}
          alt={name}
          loading="lazy"
          decoding="async"
          width="64"
          height="64"
          className="h-16 w-16 shrink-0 rounded-[18px] object-cover object-top"
        />
      ) : (
        <div className={`${mono} h-16 w-16 shrink-0 rounded-[18px] text-xl`}>{initials}</div>
      )}

      <div className="min-w-0 flex-1">
        <p className="text-secondary mb-1 text-[10px] font-bold tracking-widest uppercase">
          {role}
        </p>
        <p className="font-headline text-on-surface text-base leading-snug font-bold" title={name}>
          {displayName(name)}
        </p>
      </div>

      {party && <PartyBadge party={party} />}
    </div>
  );
}

// ── Member variant (Vogal / Membro) ───────────────────────────────────────────
function MemberSmallCard({ initials, name, role, photo, party }: MemberCardProps) {
  return (
    <div className="bg-surface border-line rounded-xl border p-5 text-center shadow-sm">
      {photo ? (
        <img
          src={photo}
          alt={name}
          loading="lazy"
          decoding="async"
          width="48"
          height="48"
          className="mx-auto mb-3 h-12 w-12 rounded-[14px] object-cover object-top"
        />
      ) : (
        <div className={`${mono} mx-auto mb-3 h-12 w-12 rounded-[14px] text-base`}>{initials}</div>
      )}

      <p className="text-tertiary mb-1.5 text-[9px] font-bold tracking-widest uppercase">{role}</p>

      <p className="font-headline text-on-surface text-sm leading-snug font-bold" title={name}>
        {displayName(name)}
      </p>

      {party && (
        <div className="mt-3 flex justify-center">
          <PartyBadge party={party} />
        </div>
      )}
    </div>
  );
}

// ── Public export ─────────────────────────────────────────────────────────────
export default function MemberCard(props: MemberCardProps) {
  if (props.variant === 'featured') return <FeaturedCard {...props} />;
  if (props.variant === 'officer') return <OfficerCard {...props} />;
  return <MemberSmallCard {...props} />;
}
